/**
 * The application shell.
 *
 * Decides between the sign-in page and the application by asking the
 * platform who is signed in, rather than by trusting anything the browser
 * holds: the session cookie is HttpOnly, so this side cannot read it and
 * should not try to infer it.
 *
 * Once signed in, the screens are routed. A route rather than a piece of
 * component state because these are places -- a reader bookmarks the
 * breadth page, opens it in a second tab, and presses Back expecting to
 * arrive where they were.
 */

import { useCallback, useState } from "react";
import { BrowserRouter, Route, Routes, useNavigate, useParams } from "react-router-dom";

import type { Account } from "@/api/client";
import { fetchAccount, fetchHello, fetchSessions, signOut } from "@/api/client";
import { AppShell, type Screen } from "@/components/AppShell";
import { KeyboardShortcuts } from "@/components/KeyboardShortcuts";
import { ReferencedPage } from "@/components/ReferencedPage";
import { useRecordPageViews } from "@/hooks/useRecordPageViews";
import { useResource } from "@/hooks/useResource";
import { ENTITIES, MARKS } from "@/lib/entities";
import { PATHS, populationPath } from "@/lib/paths";
import { ThemeProvider } from "@/lib/theme";
import { Breadth } from "@/routes/Breadth";
import { MarketCalendar } from "@/routes/MarketCalendar";
import { MarketPhases } from "@/routes/MarketPhases";
import { Company } from "@/routes/Company";
import { Compare } from "@/routes/Compare";
import { Deals } from "@/routes/Deals";
import { EarningsPage } from "@/routes/Earnings";
import { Flows } from "@/routes/Flows";
import { Indices } from "@/routes/Indices";
import { Scans } from "@/routes/Scans";
import { Screener } from "@/routes/Screener";
import { Watchlists } from "@/routes/Watchlists";
import { Sectors } from "@/routes/Sectors";
import { Fund } from "@/routes/Fund";
import { Backtest } from "@/routes/Backtest";
import { Backtests } from "@/routes/Backtests";
import { Funds } from "@/routes/Funds";
import { Future } from "@/routes/Future";
import { Futures } from "@/routes/Futures";
import { Ipo } from "@/routes/Ipo";
import { Ipos } from "@/routes/Ipos";
import { Movers } from "@/routes/Movers";
import { News } from "@/routes/News";
import { Overview } from "@/routes/Overview";
import { Population } from "@/routes/Population";
import { Profile } from "@/routes/Profile";
import { SignIn } from "@/routes/SignIn";
import { Strategies } from "@/routes/Strategies";
import { Strategy } from "@/routes/Strategy";
import { StrategyYears } from "@/routes/StrategyYears";
import { NotFound } from "@/routes/NotFound";
import { Visitors } from "@/routes/Visitors";

/** The navigation, in the order the screens are meant to be read. */
const SCREENS: Screen[] = [
  { path: PATHS.overview, label: "Overview", icon: MARKS.overview, group: "Markets", exact: true },
  { path: PATHS.breadth, label: "Breadth", icon: MARKS.breadth, group: "Markets" },
  { path: PATHS.marketPhase, label: "Market phase", icon: MARKS.phases, group: "Markets" },
  { path: PATHS.calendar, label: "Calendar", icon: MARKS.calendar, group: "Markets" },
  { path: PATHS.flows, label: "FII / DII", icon: MARKS.flows, group: "Markets" },
  { path: PATHS.deals, label: "Deals", icon: MARKS.deals, group: "Markets" },
  {
    path: PATHS.indices,
    label: "Indices",
    icon: ENTITIES.index.icon,
    group: "Markets",
    matches: ["/index/"],
  },
  {
    path: PATHS.sectors,
    label: "Sectors",
    icon: ENTITIES.sector.icon,
    group: "Markets",
    matches: ["/sector/"],
  },
  {
    path: PATHS.movers,
    label: "Movers",
    icon: MARKS.movers,
    group: "Markets",
    matches: ["/movers/"],
  },
  { path: PATHS.earnings, label: "Earnings", icon: MARKS.earnings, group: "Markets" },
  {
    path: PATHS.futures,
    label: "Futures",
    icon: ENTITIES.future.icon,
    group: "Markets",
    matches: ["/future/"],
  },
  { path: PATHS.news, label: "News", icon: MARKS.news, group: "Markets" },
  {
    path: PATHS.funds,
    label: "Funds",
    icon: ENTITIES.fund.icon,
    group: "Research",
    matches: ["/fund/"],
  },
  { path: PATHS.screen, label: "Screener", icon: MARKS.screen, group: "Research" },
  { path: PATHS.scans, label: "Scans", icon: MARKS.scans, group: "Research" },
  {
    path: PATHS.strategies,
    label: "Strategies",
    icon: MARKS.strategies,
    group: "Research",
    matches: ["/strategy/"],
  },
  {
    path: PATHS.backtests,
    label: "Backtests",
    icon: MARKS.backtests,
    group: "Research",
    matches: ["/backtest/"],
  },
  { path: PATHS.compare, label: "Compare", icon: MARKS.compare, group: "Research" },
  {
    path: PATHS.ipos,
    label: "IPOs",
    icon: ENTITIES.ipo.icon,
    group: "Research",
    matches: ["/ipo/"],
  },
  { path: PATHS.watchlists, label: "Watchlists", icon: MARKS.watchlist, group: "Mine" },
  { path: PATHS.profile, label: "Profile", icon: MARKS.profile, group: "Mine" },
];

/** Screens only the owner is offered; anyone else has no such page at all. */
const OWNER_SCREENS: Screen[] = [
  { path: PATHS.visitors, label: "Visitors", icon: MARKS.visitors, group: "Mine" },
];

/**
 * Render the application.
 *
 * @returns The shell, and whichever of the two states it is in.
 */
export function App(): React.JSX.Element {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ThemeProvider>
  );
}

/** The part that knows whether anybody is signed in. */
function Shell(): React.JSX.Element {
  const [account, setAccount] = useState<Account | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const whoami = useCallback(() => fetchAccount(), []);
  const session = useResource(whoami);
  const navigate = useNavigate();
  // Here rather than in the application proper, so the sign-in page's
  // visitors are counted as well as everyone signed in.
  useRecordPageViews();

  // Signing out takes effect here rather than waiting for the platform to
  // confirm it. The request is still made and still ends the session; what
  // would be wrong is leaving the application on screen while it travels.
  const current = signedOut ? null : (account ?? session.data);

  const leave = useCallback(() => {
    setSignedOut(true);
    setAccount(null);
    void signOut();
  }, []);

  if (session.loading && account === null && !signedOut) {
    return <Waiting />;
  }

  if (current === null) {
    return (
      <SignIn
        onSignedIn={(signedIn) => {
          setSignedOut(false);
          setAccount(signedIn);
        }}
      />
    );
  }

  return (
    <SignedIn
      account={current}
      onSignOut={leave}
      onNavigate={(path) => {
        void navigate(path);
      }}
    />
  );
}

/** The application proper, once there is somebody to show it to. */
function SignedIn({
  account,
  onSignOut,
  onNavigate,
}: {
  account: Account;
  onSignOut: () => void;
  onNavigate: (path: string) => void;
}): React.JSX.Element {
  // Asked once, for the footer: which build is running is the first thing
  // worth knowing when a screen disagrees with what the code says it does.
  const greeting = useCallback(() => fetchHello(), []);
  const hello = useResource(greeting);
  // And how far the data reaches, so a stale day shows in the frame rather
  // than being discovered in a figure.
  const latest = useCallback(() => fetchSessions(1), []);
  const sessions = useResource(latest);

  return (
    <AppShell
      account={account}
      screens={account.is_owner ? [...SCREENS, ...OWNER_SCREENS] : SCREENS}
      build={hello.data}
      latestSession={sessions.data?.[0]?.day ?? null}
      onOpenProfile={() => {
        onNavigate(PATHS.profile);
      }}
      onSignOut={onSignOut}
    >
      <KeyboardShortcuts />
      <Routes>
        <Route
          path={PATHS.overview}
          element={
            <Overview
              onOpenIndex={(key) => {
                onNavigate(populationPath("index", key));
              }}
              onOpenPopulation={(kind, key) => {
                onNavigate(populationPath(kind, key));
              }}
              onOpenBreadth={() => {
                onNavigate(PATHS.breadth);
              }}
              onOpenNews={() => {
                onNavigate(PATHS.news);
              }}
            />
          }
        />
        <Route path={PATHS.breadth} element={<Breadth />} />
        <Route path={PATHS.marketPhase} element={<MarketPhases />} />
        <Route path={PATHS.calendar} element={<MarketCalendar />} />
        <Route path={PATHS.flows} element={<Flows />} />
        <Route path={PATHS.deals} element={<Deals />} />
        <Route path={PATHS.news} element={<News />} />
        <Route path="/movers/:list" element={<Movers />} />
        <Route path={PATHS.earnings} element={<EarningsPage />} />
        <Route path={PATHS.indices} element={<Indices />} />
        <Route path={PATHS.sectors} element={<Sectors />} />
        <Route path={PATHS.screen} element={<Screener />} />
        <Route path={PATHS.scans} element={<Scans />} />
        <Route path={PATHS.strategies} element={<Strategies />} />
        <Route path={PATHS.strategyYears} element={<StrategyYears />} />
        <Route path="/strategy/:id" element={<Strategy />} />
        <Route path={PATHS.backtests} element={<Backtests />} />
        <Route path="/backtest/:id" element={<Backtest />} />
        <Route path={PATHS.compare} element={<Compare />} />
        <Route path={PATHS.watchlists} element={<Watchlists />} />
        <Route path={PATHS.ipos} element={<Ipos />} />
        <Route path="/ipo/:id" element={<IpoRoute />} />
        <Route path={PATHS.funds} element={<Funds />} />
        <Route path={PATHS.futures} element={<Futures />} />
        <Route path="/future/:key" element={<FutureRoute />} />
        <Route path="/fund/:code" element={<FundRoute />} />
        <Route
          path="/company/:ref"
          element={
            <ReferencedPage kind="company">
              {(key) => <Company instrumentKey={key} />}
            </ReferencedPage>
          }
        />
        <Route
          path="/index/:ref"
          element={
            <ReferencedPage kind="index">
              {(key) => <Population kind="index" scopeKey={key} />}
            </ReferencedPage>
          }
        />
        <Route
          path="/sector/:ref"
          element={
            <ReferencedPage kind="sector">
              {(key) => <Population kind="sector" scopeKey={key} />}
            </ReferencedPage>
          }
        />
        <Route path={PATHS.profile} element={<Profile account={account} onSignOut={onSignOut} />} />
        {account.is_owner && <Route path={PATHS.visitors} element={<Visitors />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

/**
 * Read the offering's identifier out of the path and show its page.
 *
 * @returns The page.
 */
function IpoRoute(): React.JSX.Element {
  const { id } = useParams();
  return <Ipo ipoId={id ?? ""} />;
}

/**
 * Read the scheme code out of the path and show its page.
 *
 * @returns The page.
 */
function FundRoute(): React.JSX.Element {
  const { code } = useParams();
  return <Fund schemeCode={code ?? ""} />;
}

function FutureRoute(): React.JSX.Element {
  const { key } = useParams();
  return <Future instrumentKey={key ?? ""} />;
}

/** What shows while the platform is being asked who is signed in. */
function Waiting(): React.JSX.Element {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-4">
      <img src="/brand/logo.svg" alt="" className="h-16 w-16 motion-safe:animate-pulse" />
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  );
}
